<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <p v-if="loading && !artifact" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="loadingFailed" class="text-muted">{{ _('This item is no longer available.') }}</p>
      <MaxArtifact
        v-if="artifact"
        :artifact="artifact"
        :labels="maxLabels"
        :busy="saving"
        :open-entity="openEntity"
        :can-open-entity="canOpenEntity"
        v-on:dismiss="setStatus('dismissed')"
        v-on:restore="setStatus('read')"
      />
      <div class="flex-row space-between max-artifact-footer">
        <button v-if="artifact && artifact.pdf_available" class="btn btn-default" :disabled="exporting" v-on:click="downloadPdf">
          <i class="fa fa-fw" :class="exporting ? 'fa-cog fa-spin' : 'fa-file-pdf-o'" aria-hidden="true"></i>
          {{ exporting ? _('Creating PDF...') : _('Download PDF') }}
        </button>
        <span v-else></span>
        <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import maxlabels from '~/lib/maxlabels'

export default {
  data () {
    return {
      artifact: null,
      exporting: false
    }
  },
  mixins: [components, maxlabels],
  computed: {
    artifactId () {
      return this.component && this.component.initData ? parseInt(this.component.initData.artifactId, 10) : null
    }
  },
  watch: {
    artifactId (id) {
      if (id && (!this.artifact || this.artifact.id !== id)) {
        this.loadArtifact(id)
      }
    }
  },
  methods: {
    async loadArtifact (id) {
      this.loading = true
      this.loadingFailed = false
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/maxartifacts/' + id + '.json?apikey=' + this.apiKey)
      } catch (e) {
        this.loading = false
        this.loadingFailed = true
        this.artifact = null
        return
      }
      this.loading = false
      this.artifact = response.data
      if (this.artifact.status === 'new') {
        this.markRead()
      }
    },
    async markRead () {
      try {
        await axios.put(this.baseDomain + this.shopId + '/maxartifacts/' + this.artifact.id + '.json?apikey=' + this.apiKey, { id: this.artifact.id, status: 'read' })
      } catch (e) {
        return
      }
      this.artifact = { ...this.artifact, status: 'read' }
      this.EventBus.$emit('maxArtifactUpdate', { id: this.artifact.id })
    },
    async setStatus (status) {
      this.saving = true
      let response
      try {
        response = await axios.put(this.baseDomain + this.shopId + '/maxartifacts/' + this.artifact.id + '.json?apikey=' + this.apiKey, { id: this.artifact.id, status })
      } catch (e) {
        this.saving = false
        return this.showError(e)
      }
      this.saving = false
      this.artifact = { ...this.artifact, status: response.data.status }
      this.EventBus.$emit('maxArtifactUpdate', { id: this.artifact.id })
      if (status === 'dismissed') {
        this.showNotification(this._('Dismissed. You can find it under Dismissed.'))
        this.close()
      }
    },
    async downloadPdf () {
      const artifact = this.artifact
      this.exporting = true
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/maxartifacts/' + artifact.id + '.pdf?apikey=' + this.apiKey, { responseType: 'blob' })
      } catch (e) {
        this.exporting = false
        return this.showError(await this.pdfErrorMessage(e.response && e.response.data))
      }
      const blob = response.data
      const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer())
      const isPdf = head.length >= 4 && head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46
      if (!isPdf) {
        this.exporting = false
        return this.showError(await this.pdfErrorMessage(blob))
      }
      this.exporting = false
      const slug = String(artifact.title || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 60)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = (slug || 'johku-max') + '.pdf'
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10000)
    },
    async pdfErrorMessage (body) {
      const fallback = this._('The PDF could not be created. Try again later.')
      if (!(body instanceof Blob) || !body.type.startsWith('text/plain')) {
        return fallback
      }
      const text = (await body.text()).trim()
      return text && text.length <= 300 ? text : fallback
    },
    canOpenEntity (entity) {
      if (entity.kind === 'product' || entity.kind === 'category') {
        return !!entity.id
      }
      return entity.kind === 'shop' || entity.kind === 'setting'
    },
    openEntity (entity) {
      const trail = this.componentTrail
      if (entity.kind === 'product') {
        return this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'ProductDetails',
          title: this._('Product') + ' ' + entity.id + ': ' + entity.label,
          width: 6,
          data: { productId: entity.id, trail }
        })
      }
      if (entity.kind === 'category') {
        return this.EventBus.$emit('showDetailsColumn', {
          shopId: this.shopId,
          component: 'CategoryDetails',
          title: this._('Category') + ' ' + entity.id + ': ' + entity.label,
          width: 6,
          data: { categoryId: entity.id, trail }
        })
      }
      this.EventBus.$emit('showComponent', {
        shopId: this.shopId,
        component: 'StorefrontSettings',
        title: this._('Storefront settings'),
        initData: {}
      })
    },
    close () {
      this.EventBus.$emit('hideDetailsColumn', {
        shopId: this.shopId
      })
    }
  },
  created () {
    if (this.artifactId) {
      this.loadArtifact(this.artifactId)
    }
  }
}
</script>

<style scoped>
.max-artifact-footer {
  margin-top: 16px;
}
</style>
